/**
 * Associate Recipe Modal
 *
 * Modal to select and associate an existing published recipe with a POS product.
 * Shows only published recipes (not drafts).
 */

import { useState, useMemo } from 'react';
import { Modal } from '@/components/ui';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { useRecipes } from '../hooks';
import { Search, ChefHat, Loader2, Package } from 'lucide-react';
import { useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';

interface AssociateRecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectRecipe: (recipeId: string) => void;
  currentRecipeId?: string;
}

export function AssociateRecipeModal({
  isOpen,
  onClose,
  onSelectRecipe,
  currentRecipeId,
}: AssociateRecipeModalProps) {
  const [search, setSearch] = useState('');
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const currency = useCurrency();

  // Fetch published recipes only (excludeDrafts)
  const { data, isLoading } = useRecipes({
    includeDrafts: false,
    includeArchived: false,
    search: search || undefined,
    limit: 50,
  });

  // Filter out current recipe if already associated
  const availableRecipes = useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter(recipe => recipe.id !== currentRecipeId);
  }, [data?.data, currentRecipeId]);

  const handleSelect = () => {
    if (selectedRecipeId) {
      onSelectRecipe(selectedRecipeId);
      setSelectedRecipeId(null);
      setSearch('');
    }
  };

  const handleClose = () => {
    setSelectedRecipeId(null);
    setSearch('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Associer une recette existante"
      size="lg"
    >
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            type="text"
            placeholder="Rechercher une recette..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Recipes list */}
        <div className="max-h-96 overflow-y-auto border border-neutral-200 rounded-lg">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
            </div>
          ) : availableRecipes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <ChefHat className="h-12 w-12 text-neutral-300 mb-3" />
              <p className="text-neutral-600">
                {search
                  ? 'Aucune recette trouvee'
                  : 'Aucune recette publiee disponible'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {availableRecipes.map((recipe) => (
                <button
                  key={recipe.id}
                  onClick={() => setSelectedRecipeId(recipe.id)}
                  className={`w-full p-4 text-left hover:bg-neutral-50 transition-colors ${
                    selectedRecipeId === recipe.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-dark-500 truncate">
                          {recipe.name}
                        </h4>
                        <span className="text-xs text-neutral-500 flex-shrink-0">
                          V{recipe.version}
                        </span>
                      </div>
                      {recipe.description && (
                        <p className="text-sm text-neutral-600 line-clamp-2">
                          {recipe.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        {recipe.theoreticalCost !== null ? (
                          <div className="text-xs text-neutral-500">
                            Cout: {formatCentimes(recipe.theoreticalCost, currency)}
                          </div>
                        ) : (
                          <div className="text-xs text-neutral-400">
                            Cout non calcule
                          </div>
                        )}
                        {recipe.posProducts && recipe.posProducts.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-neutral-500">
                            <Package className="h-3 w-3" />
                            {recipe.posProducts.length} produit(s) POS
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedRecipeId === recipe.id && (
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSelect}
            disabled={!selectedRecipeId}
          >
            Associer
          </Button>
        </div>
      </div>
    </Modal>
  );
}
